import { Component, DestroyRef, inject, signal } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { EmissionsFactoryApi } from '@features/emissions/services/emissions-factory-api';
import { DataEndpoint } from '@core/models/general.model';
import { EmissionsFactory, EmissionsSourcesFactoryPaginatedModel, EmissionsFactoryRequestModel, EmissionsSourcesFactory } from '@features/emissions/models/emissions-factory.model';
import { HttpErrorResponse } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { BlockUI } from "primeng/blockui";
import { ProgressSpinner } from "primeng/progressspinner";
import { EmissionsFactorsFilter } from '@features/emissions/components/emissions-factors-filter/emissions-factors-filter';
import { EmissionsFactorsList } from '@features/emissions/components/emissions-factors-list/emissions-factors-list';
import { PaginatorState } from 'primeng/paginator';
import { HttpClient } from '@angular/common/http';
import { EmissionsSourcesApi } from '@features/emissions/services/emissions-sources-api.';
import { EmissionsSourcesList } from '@features/emissions/components/emissions-sources-list/emissions-sources-list.component';
import { FormFooter } from '@shared/components/form-footer/form-footer';
import { debounceTime, fromEvent } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BusinessStateService } from '@core/services/business-state.service';


type SourcesMapGroup = Record<string, string>;
type categoriesMapGroup = Record<number, string>;
type SourcesMap = {
  categories: categoriesMapGroup;
  sources: SourcesMapGroup;
  references: SourcesMapGroup;
  types: SourcesMapGroup;
  method: SourcesMapGroup;
  variations: SourcesMapGroup;
};

type SelectionKind = 'All' | 'Group' | 'Business' | 'Office';
type BusinessSelection = { id: string; kind: SelectionKind; businessId?: string; officeId?: string };

@Component({
  selector: 'app-emissions-sources',
  imports: [
    ButtonModule,
    EmissionsFactorsFilter,
    EmissionsSourcesList,
    ToastModule,
    BlockUI,
    ProgressSpinner,
    FormFooter
  ],
  templateUrl: './emissions-sources.component.html',
  styleUrls: ['./emissions-sources.component.scss'],
  providers: [MessageService]
})
export class EmissionsSources {
  private http = inject(HttpClient);
  private messageService = inject(MessageService);
  private emissionsSourcesApi = inject(EmissionsSourcesApi);
  private readonly businessStateService = inject(BusinessStateService);

  isAllSelected = false;
  isIndeterminate = false;

  //unsaved changes
  dirtyChanges = new Map<string, Partial<EmissionsSourcesFactory>>();

  selectedSources: EmissionsSourcesFactory[] = [];
  initialSelectedIds = new Set<string>();

  sourcesMap: SourcesMap = {
    categories: {},
    sources: {},
    references: {},
    types: {},
    method: {},
    variations: {}
  };

  // ✅ Datos
  private allData: EmissionsSourcesFactory[] = [];   // TODO lo que llega de API
  filteredData: EmissionsSourcesFactory[] = [];      // SOLO lo visible (página actual)

  // ✅ UI state
  isLoading = false;
  sortBy: string | null = null;
  sortOrder: 'asc' | 'desc' | null = null;

  data: EmissionsSourcesFactory[] = [];
  totalRecords: number = 0;
  first: number = 0;

  // Paginación local
  pageNumber = 1;
  pageSize = 10;

  // Filtro local
  searchTerm = '';

  businessId: string = '';
  officeId: string = '';
  isOffice: boolean = false
  isBusiness: boolean = false

  private destroyRef = inject(DestroyRef);
  private getSelectionKind(): 'all' | 'group' | 'business' | 'office' | '' {
    const raw = localStorage.getItem('businessSelection');
    if (!raw) return '';
    try {
      const sel = JSON.parse(raw) as { kind?: string };
      return (sel.kind ?? '').toString().toLowerCase() as any;
    } catch {
      return '';
    }
  }


  private loadSelectionFromStorage() {
    this.businessId = '';
    this.officeId = '';
    this.isBusiness = false;
    this.isOffice = false;

    const selected = this.businessStateService.getSelectedBusiness();

    const kind = selected?.data?.kind;

    this.isBusiness = kind.toLowerCase() === 'business';
    this.isOffice = kind.toLowerCase() === 'office';

    this.businessId = this.isBusiness ? selected?.data?.id : '';
    this.officeId = this.isOffice ? selected?.data?.id : '';
  }




  ngOnInit() {

    this.loadSelectionFromStorage();

    // escucha cambios desde el header (misma pestaña)
    fromEvent<CustomEvent<BusinessSelection>>(window, 'businessSelectionChanged')
      .pipe(debounceTime(100), takeUntilDestroyed(this.destroyRef))
      .subscribe((evt) => {
        const sel = evt.detail;

        this.businessId = sel.kind === 'Business' ? sel.id : '';
        this.officeId = sel.kind === 'Office' ? sel.id : '';

        this.isBusiness = sel.kind === 'Business';
        this.isOffice = sel.kind === 'Office';

        this.loadSelectionFromStorage();
        this.loadAllData();
      });


    // 1) Carga el JSON plano
    this.http.get<SourcesMap>('assets/data/sources.json').subscribe({
      next: (map) => {
        this.sourcesMap = map;
        // 2) Trae TODA la data (una vez)
        this.loadAllData();
      },
      error: () => {
        // fallback seguro
        this.sourcesMap = {
          categories: {},
          sources: {},
          references: {},
          types: {},
          method: {},
          variations: {}
        };
        this.loadAllData();
      }
    });

  }

  loadAllData() {
    const selectedBusiness = this.businessStateService.getSelectedBusiness();

    if (selectedBusiness?.data) {
      const { kind, id } = selectedBusiness.data;

      if (!this.businessId && kind.toLowerCase() === 'business') {
        this.businessId = id;
      }

      if (!this.officeId && kind.toLowerCase() === 'office') {
        this.officeId = id;
      }
    }

    if (!this.businessId && !this.officeId) {
      this.allData = [];
      this.filteredData = [];
      this.totalRecords = 0;
      return;
    }

    this.isLoading = true;
    this.allData = [];

    const pageSizeForFetch = 500; // sube/baja según volumen
    const baseRequest: EmissionsFactoryRequestModel = {
      businessId: this.businessId,
      officeId: this.officeId || undefined,
      pageNumber: 1,
      pageSize: pageSizeForFetch,
      searchTerm: undefined,
      sortBy: undefined,
      sortOrder: undefined,
    };

    this.fetchPage(baseRequest, 0);
  }

  private fetchPage(request: EmissionsFactoryRequestModel, totalCountSoFar: number) {

    this.emissionsSourcesApi.getEmissionsSourcesByBusiness(request).subscribe({
      next: (response: DataEndpoint<EmissionsSourcesFactoryPaginatedModel>) => {
        if (response.Status !== 200) {
          this.isLoading = false;
          return;
        }

        const records = response.Data?.records ?? [];
        const totalCount = response.Data?.pagination?.totalCount ?? 0;

        this.allData.push(...records);

        if (this.allData.length < totalCount && records.length > 0) {
          this.fetchPage({ ...request, pageNumber: (request.pageNumber ?? 1) + 1 }, totalCount);
          return;
        }

        // listo
        this.isLoading = false;

        this.selectedSources = this.allData.filter(x => x.IsAssigned)
        this.initialSelectedIds = new Set(this.selectedSources.map(x => x.Id))

        this.checkIfAllSelected();

        // reset paginación local
        this.pageNumber = 1;
        this.first = 0;

        // construir vista
        this.applyLocalView();
      },
      error: (error: HttpErrorResponse) => {
        this.isLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error?.error?.Message ?? 'Error al cargar datos'
        });
      }
    });
  }

  private applyLocalView() {
    const filtered = this.applyFilter(this.allData, this.searchTerm);
    const sorted = this.applySort(filtered, this.sortBy, this.sortOrder);

    this.totalRecords = sorted.length;

    const start = this.first ?? 0;
    const end = start + (this.pageSize ?? 10);

    this.filteredData = sorted.slice(start, end);
    this.checkIfAllSelected();
  }

private normalizeText(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

private applyFilter(data: EmissionsSourcesFactory[], term: string): EmissionsSourcesFactory[] {
  const t = this.normalizeText((term ?? '').trim());
  if (!t) return data;

  // Helper: comprueba si el término coincide con la key o con el label traducido
  const matches = (key: string | null | undefined, translated: string): boolean => {
    const normalizedKey = this.normalizeText(String(key ?? ''));
    const normalizedLabel = this.normalizeText(translated);
    return normalizedKey.includes(t) || normalizedLabel.includes(t);
  };

  return data.filter(f => {
    const categoryKey = String(f.Category ?? '');
    const categoryLabel = (
      this.sourcesMap.categories?.[f.Category as any] ??
      categoryKey
    ).toString();

    // Columna "Fuente de emisión" → usa variations
    const variationKey = String((f as any).Variation ?? '');
    const variationLabel = (
      this.sourcesMap.variations?.[variationKey] ??
      variationKey
    ).toString();

    // Columna "Referencia de actividad" → usa references
    const referenceKey = String(f.Reference ?? '');
    const referenceLabel = (
      this.sourcesMap.references?.[referenceKey] ??
      referenceKey
    ).toString();

    return (
      matches(categoryKey, categoryLabel) ||
      matches(variationKey, variationLabel) ||
      matches(referenceKey, referenceLabel)
    );
  });
}


  private applySort(data: EmissionsSourcesFactory[], sortBy: string | null, sortOrder: 'asc' | 'desc' | null) {
    if (!sortBy || !sortOrder) return data;

    const dir = sortOrder === 'asc' ? 1 : -1;

    return [...data].sort((a, b) => {
      const va = this.getSortValue(a, sortBy);
      const vb = this.getSortValue(b, sortBy);

      // numbers
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;

      // booleans
      if (typeof va === 'boolean' && typeof vb === 'boolean') return ((va === vb) ? 0 : (va ? 1 : -1)) * dir;

      const sa = (va ?? '').toString();
      const sb = (vb ?? '').toString();
      return sa.localeCompare(sb, 'es', { sensitivity: 'base' }) * dir;
    });
  }

  private getSortValue(f: EmissionsSourcesFactory, key: string): string | number {
    switch (key) {
      case 'Category':
        return Number(f.Category ?? -1);

      case 'Source':
        return this.sourcesMap.sources[String(f.Source)] ?? String(f.Source ?? '');

      case 'Reference':
        return this.sourcesMap.references[String(f.Reference)] ?? String(f.Reference ?? '');

      case 'Type':
        return this.sourcesMap.types[String(f.Type)] ?? String(f.Type ?? '');

      case 'Method':
        return this.sourcesMap.method[String(f.Method)] ?? String(f.Method ?? '');

      default:
        return '';
    }
  }


  handlePageChange(event: PaginatorState) {
    this.pageNumber = (event.page ?? 0) + 1;
    this.pageSize = event.rows ?? 10;
    this.first = event.first ?? 0;
    this.applyLocalView();

  }

  handleToggleSort(columnName: string) {
    if (this.sortBy === columnName) {
      this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortBy = columnName;
      this.sortOrder = 'asc';
    }
    this.applyLocalView();
  }

  handleFilter(search: string) {
    this.searchTerm = search;
    this.pageNumber = 1;
    this.first = 0;
    this.applyLocalView();

  }

  handleClearFilters() {
    this.searchTerm = '';
    this.pageNumber = 1;
    this.first = 0;
    this.applyLocalView();

  }

  handleToggleAll(checked: boolean) {
    if (checked) {
      this.selectAll();
    } else {
      this.clearSelection();
    }
  }

  handleToggleRow(event: { id: string; checked: boolean }) {
    const item = this.allData.find(x => x.Id === event.id);
    if (item && this.canEditRow(item)) {
      item.IsAssigned = event.checked;
    }
    
    this.selectedSources = this.allData.filter(x => x.IsAssigned);
    this.checkIfAllSelected();
  }

  selectAll() {
    const filtered = this.applyFilter(this.allData, this.searchTerm);
    filtered.forEach(item => {
      if (this.canEditRow(item)) {
        item.IsAssigned = true;
      }
    });

    this.selectedSources = this.allData.filter(x => x.IsAssigned);
    this.checkIfAllSelected();
  }

  clearSelection() {
    const filtered = this.applyFilter(this.allData, this.searchTerm);
    filtered.forEach(item => {
      if (this.canEditRow(item)) {
        item.IsAssigned = false;
      }
    });

    this.selectedSources = this.allData.filter(x => x.IsAssigned);
    this.checkIfAllSelected();
  }

  checkIfAllSelected() {
    const filtered = this.applyFilter(this.allData, this.searchTerm);
    const editableItems = filtered.filter(x => this.canEditRow(x));
    if (editableItems.length === 0) {
      this.isAllSelected = false;
      this.isIndeterminate = false;
      return;
    }
    
    const selectedEditableCount = editableItems.filter(x => x.IsAssigned).length;
    
    this.isAllSelected = selectedEditableCount === editableItems.length;
    this.isIndeterminate = selectedEditableCount > 0 && selectedEditableCount < editableItems.length;
  }

  handleChange(event: { id: string; changes: Partial<EmissionsSourcesFactory> }) {
    const current = this.dirtyChanges.get(event.id) ?? {};
    this.dirtyChanges.set(event.id, { ...current, ...event.changes });
  }

  hasUnsavedChanges(): boolean {
    return this.dirtyChanges.size > 0;
  }


  get unsavedChangesCount(): number {
    const currentIds = new Set(this.selectedSources.map(x => x.Id));

    let added = 0;
    let removed = 0;

    currentIds.forEach(id => {
      if (!this.initialSelectedIds.has(id)) added++;
    });

    this.initialSelectedIds.forEach(id => {
      if (!currentIds.has(id)) removed++;
    });

    return added + removed;
  }

  private isInherited(item: EmissionsSourcesFactory): boolean {
    return (item as any).IsInherited === true;
  }

  private canEditRow(item: EmissionsSourcesFactory): boolean {
    // Business: puede editar TODO
    if (this.isBusiness) return true;

    // Office: NO puede editar heredados
    if (this.isOffice) return !this.isInherited(item);

    // Otros: no editable
    return false;
  }

  onSelectionChange(selected: EmissionsSourcesFactory[]) {
    // Ya no se utiliza p-table selectionChange nativo, pero lo dejamos por compatibilidad o se puede borrar
  }


  private getCurrentSelectedIds(): Set<string> {
    return new Set(this.allData.filter(x => x.IsAssigned).map(x => x.Id));
  }

  private buildSavePayload(): SaveEmissionsSourcesRequest {
    const currentIds = this.getCurrentSelectedIds();

    let added = Array.from(currentIds).filter(id => !this.initialSelectedIds.has(id));
    let removed = Array.from(this.initialSelectedIds).filter(id => !currentIds.has(id));

    const byId = new Map(this.allData.map(x => [x.Id, x]));

    // En Office solo se permiten cambios en NO heredados
    if (this.isOffice) {
      added = added.filter(id => {
        const item = byId.get(id);
        return item ? !this.isInherited(item) : false;
      });

      removed = removed.filter(id => {
        const item = byId.get(id);
        return item ? !this.isInherited(item) : false;
      });
    }

    // En Business se permite todo (added/removed sin filtrar)
    return {
      CompanyId: this.isBusiness ? this.businessId : '',
      OfficeId: this.isOffice ? this.officeId : '',
      AddedSourceIds: added,
      RemovedSourceIds: removed,
    };
  }





  onSave(): void {
    const payload = this.buildSavePayload();

    const hasChanges = payload.AddedSourceIds.length > 0 || payload.RemovedSourceIds.length > 0;
    if (!hasChanges) {
      this.messageService.add({
        severity: 'info',
        summary: 'Sin cambios',
        detail: 'No hay cambios para guardar'
      });
      return;
    }

    this.isLoading = true;

    console.log('SAVE PAYLOAD:', payload);

    // 👇 ajusta el nombre del método según tu API real
    this.emissionsSourcesApi.saveEmissionsSources(payload).subscribe({
      next: (resp: DataEndpoint<any>) => {
        this.isLoading = false;

        if (resp?.Status !== 200) {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: resp?.Message ?? 'No se pudo guardar'
          });
          return;
        }

        // ✅ actualizar baseline para que unsavedChangesCount vuelva a 0
        const currentIds = this.getCurrentSelectedIds();
        this.initialSelectedIds = new Set(currentIds);

        // si estabas usando dirtyChanges para otras cosas
        this.dirtyChanges.clear();

        this.messageService.add({
          severity: 'success',
          summary: 'Guardado',
          detail: 'Cambios guardados correctamente'
        });
      },
      error: (error: HttpErrorResponse) => {
        this.isLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error?.error?.Message ?? 'Error al guardar'
        });
      }
    });
  }



}
